import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { t, tl } from "../../../components/translate";
import CertificatePrinter from "../Certificate/CertificatePrinter";
import { Client } from "../../../interfaces/ClientInterface";
import { Training } from "../../../interfaces/TrainingInterface";
import TrainingCreateEdit from "./TrainingCreateEdit";
import { useDeleteTraining } from "../../../hooks/query/useTraining";
import OkhatiDialog from "../../../components/Dialog/Dialog";

export interface TrainingProps {
  training: Training;
  client: Client;
  onRefresh?: () => void;
}

const TrainingCard: React.FC<TrainingProps> = ({ training, client, onRefresh }) => {
  const [deleting, setDeleting] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { mutateAsync: deleteTrainingMutate } = useDeleteTraining();

  const handleConfirmDelete = async () => {
    if (!training.id) return;
    setDeleting(true);
    try {
      await deleteTrainingMutate(training.id);
      setIsDeleteDialogOpen(false);
      onRefresh?.();
    } finally {
      setDeleting(false);
    }
  };

  const { name, certificateNumber, duration, startDate, endDate, issueDate, signatories } =
    training;

  const durationText =
    duration?.value !== undefined && duration?.value !== null && duration.value !== ""
      ? `${duration.value} ${duration.unit || "Hours"}`
      : "";

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          borderColor: "divider",
          transition: "box-shadow 0.2s ease-in-out"
        }}
      >
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
                {name || t("CreateClient.Certificate")}
              </Typography>
              {certificateNumber && (
                <Chip
                  label={`#${certificateNumber}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>
          }
          subheader={
            durationText ? (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {`${durationText} Training`}
              </Typography>
            ) : undefined
          }
          action={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CertificatePrinter client={client} training={training} />
              <Tooltip title="Edit Training">
                <IconButton
                  size="small"
                  onClick={() => setIsEditOpen(true)}
                  data-testmation="editTrainingButton"
                  sx={{
                    width: 32,
                    height: 32,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    "&:hover": {
                      borderColor: "primary.main"
                    }
                  }}
                >
                  <EditIcon fontSize="small" color="action" />
                </IconButton>
              </Tooltip>
              {training.id && (
                <Tooltip title="Delete Training">
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setIsDeleteDialogOpen(true)}
                      disabled={deleting}
                      data-testmation="deleteTrainingButton"
                      sx={{
                        width: 32,
                        height: 32,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        "&:hover": {
                          borderColor: "error.main"
                        }
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          }
          sx={{ pb: 1 }}
        />
        <Divider />
        <CardContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            {name && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                  {tl("CreateClient.TrainingName")}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {name}
                </Typography>
              </Grid>
            )}

            {certificateNumber && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                  {tl("CreateClient.CertificateNumber")}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {certificateNumber}
                </Typography>
              </Grid>
            )}

            {durationText && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                  {tl("CreateClient.TrainingHours")}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {durationText}
                </Typography>
              </Grid>
            )}

            {startDate && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                  {tl("startDate")}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {typeof startDate === "string"
                    ? startDate.slice(0, 10)
                    : new Date(startDate).toISOString().slice(0, 10)}
                </Typography>
              </Grid>
            )}

            {endDate && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                  {tl("completionDate")}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {typeof endDate === "string"
                    ? endDate.slice(0, 10)
                    : new Date(endDate).toISOString().slice(0, 10)}
                </Typography>
              </Grid>
            )}

            {issueDate && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                  {tl("certificateIssueDate")}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {typeof issueDate === "string"
                    ? issueDate.slice(0, 10)
                    : new Date(issueDate).toISOString().slice(0, 10)}
                </Typography>
              </Grid>
            )}

            {signatories && signatories.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                  Signatories
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                  {signatories.map((sig, idx) => (
                    <Chip
                      // eslint-disable-next-line react/no-array-index-key
                      key={idx}
                      size="small"
                      variant="outlined"
                      label={`${sig.role}: ${sig.name}`}
                    />
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {isEditOpen && (
        <TrainingCreateEdit
          clientId={client.id}
          training={training}
          onClose={() => setIsEditOpen(false)}
          afterSave={() => {
            setIsEditOpen(false);
            onRefresh?.();
          }}
        />
      )}

      {isDeleteDialogOpen && (
        <OkhatiDialog
          title="Delete Training"
          description="Are you sure you want to delete this training?"
          next={handleConfirmDelete}
          cancel={() => setIsDeleteDialogOpen(false)}
          readMode={false}
          isLoading={deleting}
        />
      )}
    </>
  );
};

export default TrainingCard;
